import { Link } from "react-router-dom";
import { COMPANY } from "../../lib/company";

/**
 * Why a brand still needs a human creator in an AI-image world.
 *
 * This is the argument page for the creative partnership: {@link Graphaize} is the
 * partner it hands off to. It sits on the public marketing surface and therefore
 * reuses the Landing page's section vocabulary exactly — `.hero`, `.strip`,
 * `.band` / `.band.alt`, `.works-band` steps, the feature `.grid`, `.faq` and
 * `.cta`. Nothing here invents a second visual language, and `PublicLayout`
 * already supplies the header, the footer and the `<main>`.
 *
 * The claims are deliberately about craft, not about measured outcomes. We do not
 * have conversion studies of our own, so the page argues from what the two kinds
 * of work actually are rather than from numbers we would have to invent.
 */

/** Where AI stops being enough and a person starts earning their fee. */
const STAGES = [
  {
    n: "01",
    title: "AI gives you raw material",
    body: "A prompt returns a hundred usable images in a minute. That is a genuine superpower for drafts, moodboards and variations — and it is where most brands now start.",
    tag: "Fast · Cheap · Infinite variations",
  },
  {
    n: "02",
    title: "A creator gives it a point of view",
    body: "Which of the hundred is on-brand? What is the idea underneath the picture? A specialist cuts ninety-nine, fixes the one, and makes it say something only your business can say.",
    tag: "Taste. Judgement. An actual idea.",
  },
  {
    n: "03",
    title: "Your brand starts compounding",
    body: "Consistent work recognisably from the same hand builds recall. Scattered AI output, however polished each piece is, resets that recognition every post.",
    tag: "Recognisable. Ownable. Yours.",
  },
];

/** What the specialist brings that the model does not have. */
const HUMAN_EDGE = [
  {
    icon: "◈",
    title: "Brand consistency, post after post",
    body: "A model has no memory of your last campaign. A creator holds your palette, your type, your tone and your crop conventions in their head, so the twentieth post still looks like it came from you.",
  },
  {
    icon: "✦",
    title: "Taste is the scarce part",
    body: "Generation is free now; choosing well is not. Knowing which image is merely competent and which one is worth your audience's attention is the whole job, and it is the part that has not been automated.",
  },
  {
    icon: "❝",
    title: "A story, not a picture",
    body: "Campaigns work when the images carry an argument across a sequence. A creator plans the arc — hook, proof, ask — and makes each frame do its share of it.",
  },
  {
    icon: "◉",
    title: "Context a model cannot see",
    body: "Your market, your season, the local festival, the thing your competitor did last week, the sensitivity that would embarrass you. Judgement about the moment is human work.",
  },
  {
    icon: "⚖",
    title: "Rights, authenticity and safety",
    body: "Someone has to be accountable for what you publish: likeness and trademark risk, platform disclosure rules, and claims that must stay true. A person signs off; a prompt cannot.",
  },
  {
    icon: "↻",
    title: "A feedback loop that learns",
    body: "A creator reads what actually performed on your Pages and channels and changes the next batch because of it. That loop is where a brand's visual language is really written.",
  },
];

/** The honest division of labour, stated as two lists rather than a straw man. */
const SPLIT = {
  ai: [
    "Drafting fifty options before lunch",
    "Backgrounds, textures, clean-up and fills",
    "Resizing one idea to every aspect ratio",
    "First-pass captions and alt text",
    "Moodboards and rough visual direction",
  ],
  human: [
    "Deciding what the campaign is actually about",
    "Keeping twenty posts recognisably one brand",
    "Judging what is good enough to publish",
    "Reading the room — culture, timing, tone",
    "Owning the claims, rights and sign-off",
  ],
};

const FAQ = [
  {
    q: "Are you saying we should not use AI?",
    a: "The opposite. Use it for everything it is good at — drafts, variations, clean-up, resizing. The mistake is treating the draft as the deliverable. AI compresses the production time; it does not supply the idea, the consistency or the accountability.",
  },
  {
    q: "Our AI images look fine. Why pay a specialist?",
    a: "Individually they usually do. The problem shows up across a quarter: twenty fine images that do not look related, carry no argument and leave no impression. Recall comes from repetition of a recognisable style, and that is a decision someone has to make and keep making.",
  },
  {
    q: "What does a content creator actually do for us?",
    a: "Sets the visual system — palette, type, crop, tone — then produces to it: explainer videos, infographics, promotional and educational pieces, pitch decks and social creative. They also decide what to make next based on what performed.",
  },
  {
    q: "How does this fit with Simino Social?",
    a: (
      <>
        {COMPANY.product} is the publishing side: compose once, schedule, and follow
        every destination separately. A creator supplies what goes into it. The two
        are different jobs, which is why we point at a specialist studio rather than
        pretending a scheduler can art-direct your brand. See{" "}
        <Link to="/how-it-works">how publishing works</Link>.
      </>
    ),
  },
];

export function ContentCreator() {
  return (
    <>
      <section className="hero">
        <div className="hero-inner">
          <div className="hero-copy">
            <p className="hero-badge"><span aria-hidden="true">✦</span> Creative partners</p>
            <h1>
              <span>AI can make the image.</span>
              <span className="hero-gradient-text">A human makes it</span>
              <span>mean something.</span>
            </h1>
            <p className="lede">
              Generation is free now. Judgement is not. A specialist content creator is what
              turns a folder of plausible pictures into a brand your audience recognises —
              and the reason some feeds shine while others just post.
            </p>
            <div className="hero-cta">
              <Link to="/partners/graphaize" className="btn-link primary lg">
                Meet our creative partner <span aria-hidden="true">→</span>
              </Link>
              <Link to="/support" className="btn-link lg">
                Talk to us <span aria-hidden="true">◉</span>
              </Link>
            </div>
            <div className="hero-benefits" aria-label="What a specialist adds">
              <span><i className="benefit-icon violet" aria-hidden="true">◈</i> Consistent<br />brand system</span>
              <span><i className="benefit-icon blue" aria-hidden="true">❝</i> Story, not<br />just pictures</span>
              <span><i className="benefit-icon green" aria-hidden="true">⚖</i> Accountable<br />sign-off</span>
            </div>
          </div>

          <div className="hero-visual">
            <section className="partner-panel partner-panel-hero" aria-labelledby="cc-split-title">
              <p className="eyebrow">The honest division of labour</p>
              <h2 id="cc-split-title">Let the machine draft. Let a person decide.</h2>
              <div className="partner-scales">
                <div className="partner-scale">
                  <h3><span className="benefit-icon blue" aria-hidden="true">◇</span> AI is great at</h3>
                  <ul>
                    {SPLIT.ai.map(item => <li key={item}>{item}</li>)}
                  </ul>
                </div>
                <div className="partner-scale is-human">
                  <h3><span className="benefit-icon violet" aria-hidden="true">✦</span> Only a human</h3>
                  <ul>
                    {SPLIT.human.map(item => <li key={item}>{item}</li>)}
                  </ul>
                </div>
              </div>
              <p className="partner-panel-note">
                Both columns are real work. The brands that pull ahead are the ones
                that stop confusing the first for the second.
              </p>
            </section>
          </div>
        </div>
      </section>

      <section className="strip">
        <div className="strip-inner">
          <div className="platforms-title"><i aria-hidden="true">✦</i><span>What a specialist brings</span></div>
          <div className="partner-tags">
            <span className="partner-tag">Visual system</span>
            <span className="partner-tag">Art direction</span>
            <span className="partner-tag">Storytelling</span>
            <span className="partner-tag">Cultural context</span>
            <span className="partner-tag">Rights &amp; sign-off</span>
            <span className="partner-tag">Performance feedback</span>
          </div>
        </div>
      </section>

      <section className="band works-band">
        <div className="band-inner">
          <div className="works-head">
            <p className="hero-badge"><span aria-hidden="true">✦</span> Draft. Direct. Compound.</p>
            <h2 className="section-title">Where AI stops, a <span>creator starts</span></h2>
            <p>The three stages of every piece of content that ends up working.</p>
          </div>
          <div className="steps">
            {STAGES.map(s => (
              <div className="step" key={s.n}>
                <span className="step-n">{s.n}</span>
                <h3>{s.title}</h3>
                <p>{s.body}</p>
                <span className="step-tag"><i aria-hidden="true">✓</i>{s.tag}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="band alt features-band">
        <div className="band-inner">
          <h2 className="section-title">What the human touch actually adds</h2>
          <p className="lede">
            Six things a prompt cannot do for you, whatever the model is capable of drawing.
          </p>
          <div className="grid">
            {HUMAN_EDGE.map(f => (
              <div className="feature" key={f.title}>
                <span className="feature-icon" aria-hidden="true">{f.icon}</span>
                <h3>{f.title}</h3>
                <p>{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="band">
        <div className="band-inner narrow">
          <h2 className="section-title">Make your brand shine, not just post</h2>
          <p className="lede">
            A feed that glows has a person behind it making unglamorous decisions over and
            over: this crop, not that one; this blue, every time; this idea is worth a
            campaign and that one is a nice picture. None of it is visible in any single
            post, and all of it is why the account is memorable a year later.
          </p>
          <p>
            That is the work a specialist content creator does. {COMPANY.product} then
            carries it to Facebook Pages, Instagram Business accounts and YouTube, with a
            separate, honest status for every destination — so the craft is not lost at the
            last step.
          </p>
          {/* The handoff, with the partner's own branding on it — a name and a
              logo are more use to a reader here than another anonymous button. */}
          <aside className="partner-graphaize gz-handoff" aria-label="Our creative partner">
            <p className="gz-logo-plate">
              <img src="/partners/graphaize-logo.png" alt="Graphaize logo" width="293" height="50" />
            </p>
            <div>
              <h3>The studio we point brands at</h3>
              <p>
                Graphaize have been making explainer and educational video, infographics,
                pitch decks and social creative since 2015 &mdash; the work this page argues
                you still need a person for.
              </p>
              <Link to="/partners/graphaize" className="btn-link primary">
                See their profile <span aria-hidden="true">&rarr;</span>
              </Link>
            </div>
          </aside>
        </div>
      </section>

      <section className="band alt">
        <div className="band-inner narrow">
          <h2 className="section-title">Questions</h2>
          <div className="faq">
            {FAQ.map(f => (
              <details key={f.q}>
                <summary>{f.q}</summary>
                <p>{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="cta">
        <div className="cta-inner">
          <span className="cta-social cta-instagram" aria-hidden="true">◎</span>
          <span className="cta-social cta-facebook" aria-hidden="true">f</span>
          <span className="cta-social cta-youtube" aria-hidden="true">▶</span>
          <h2>Ready to give your brand a human voice?</h2>
          <p>
            Bring in a specialist for the ideas and the art direction, and let {COMPANY.product}
            {" "}handle composing, scheduling and publishing across every channel.
          </p>
          <Link to="/partners/graphaize" className="btn-link primary lg">
            Meet our creative partner <span aria-hidden="true">→</span>
          </Link>
          <div className="cta-assurances" aria-label="What to expect">
            <span><i aria-hidden="true">✓</i>Brand-consistent work</span>
            <span><i aria-hidden="true">✓</i>Specialist art direction</span>
            <span><i aria-hidden="true">✓</i>Published from one place</span>
          </div>
        </div>
      </section>
    </>
  );
}
